import { Shield, Lock, Settings, Database, FileText } from 'lucide-react';

import Docker from '@/assets/ico/vendor/docker.svg?c';
import Kubernetes from '@/assets/ico/vendor/kubernetes.svg?c';
import type { Environment } from '@/react/portainer/environments/types';
import type { EnvironmentGroup } from '@/react/portainer/environments/environment-groups/types';
import { pluralize } from '@/portainer/helpers/strings';

import { BoxSelector, BoxSelectorOption } from '@@/BoxSelector';
import { Badge } from '@@/Badge';
import { FormSectionTitle } from '@@/form-components/FormSectionTitle';

import {
  PolicyType,
  PolicyCategory,
  EnvironmentTypeFilter,
  CategoryFilter,
  PolicyTemplate,
  isPolicyType,
} from '../../types';
import { getCompatibleGroupsCount } from '../../utils/policyTypeUtils';
import {
  getAllPolicyTypesMetadata,
  PolicyTypeMetadata,
} from '../../utils/policy-types-metadata';
import { filterPolicyTypes } from '../../utils/filterPolicyTypes';
import { usePolicyTemplates } from '../../queries/usePolicyTemplates';

import { PolicyTypeToolbar } from './PolicyTypeToolbar';
import {
  filterTemplates,
  getAvailableEnvironmentTypes,
  getAvailableCategories,
  buildEnvironmentFilterOptions,
  buildCategoryFilterOptions,
} from './policyTypeSelectorUtils';

/** When total environments exceed this, we don't show compatible groups count (only 100 envs are used for the count). */
const COMPATIBLE_GROUPS_COUNT_MAX_ENVIRONMENTS = 100;

interface Props {
  value: PolicyType | null;
  onChange: (policyType: PolicyType) => void;
  onTemplateSelect?: (template: PolicyTemplate | null) => void;
  selectedTemplateId?: string | null;
  environments: Environment[];
  groups: EnvironmentGroup[];
  /** When above 100, compatible groups count is not shown (only first 100 envs are used for that count). */
  totalEnvironmentCount?: number;
  environmentFilter: EnvironmentTypeFilter;
  onEnvironmentFilterChange: (filter: EnvironmentTypeFilter) => void;
  categoryFilter: CategoryFilter;
  onCategoryFilterChange: (filter: CategoryFilter) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
}

export function PolicyTypeSelector({
  value,
  onChange,
  onTemplateSelect,
  selectedTemplateId,
  environments,
  groups,
  totalEnvironmentCount,
  environmentFilter,
  onEnvironmentFilterChange,
  categoryFilter,
  onCategoryFilterChange,
  searchQuery,
  onSearchQueryChange,
}: Props) {
  const policyTypes = getAllPolicyTypesMetadata();
  const templatesQuery = usePolicyTemplates();
  const templates = templatesQuery.data || [];

  const showCompatibleGroupsCount =
    totalEnvironmentCount === undefined ||
    totalEnvironmentCount <= COMPATIBLE_GROUPS_COUNT_MAX_ENVIRONMENTS;

  // Filter policy types based on selected environment type, category, and search
  const filteredPolicyTypes = filterPolicyTypes(
    policyTypes,
    environmentFilter,
    categoryFilter,
    searchQuery
  );

  // Filter templates based on current filters
  const filteredTemplates = filterTemplates(
    templates,
    environmentFilter,
    categoryFilter,
    searchQuery
  );

  // Group by environment type (Kubernetes/Docker), then by policy type
  const environmentSections = groupByEnvironmentType(
    filteredPolicyTypes,
    filteredTemplates,
    environments,
    groups,
    showCompatibleGroupsCount
  );

  const hasNoPolicyTypes = filteredPolicyTypes.length === 0;

  const availableEnvTypes = getAvailableEnvironmentTypes(policyTypes);
  const availableCategories = getAvailableCategories(policyTypes);

  const environmentFilterOptions =
    buildEnvironmentFilterOptions(availableEnvTypes);
  const categoryFilterOptions = buildCategoryFilterOptions(availableCategories);

  return (
    <>
      <FormSectionTitle>Policy type</FormSectionTitle>
      <PolicyTypeToolbar
        searchQuery={searchQuery}
        onSearchQueryChange={onSearchQueryChange}
        environmentFilter={environmentFilter}
        environmentFilterOptions={environmentFilterOptions}
        onEnvironmentFilterChange={onEnvironmentFilterChange}
        categoryFilter={categoryFilter}
        categoryFilterOptions={categoryFilterOptions}
        onCategoryFilterChange={onCategoryFilterChange}
      />
      {hasNoPolicyTypes ? (
        <div className="text-sm text-muted">
          No policy types match the current filters. Try removing filters to see
          more options.
        </div>
      ) : (
        environmentSections.map((envSection) => (
          <EnvironmentSection
            key={envSection.environmentType}
            section={envSection}
            selectedValue={
              selectedTemplateId
                ? `template:${selectedTemplateId}`
                : value || ''
            }
            onSelect={(selectedValue) => {
              // Check if the selected value is a template
              if (selectedValue.startsWith('template:')) {
                const templateId = selectedValue.replace('template:', '');
                const template = templates.find((t) => t.id === templateId);
                if (template) {
                  onChange(template.type);
                  onTemplateSelect?.(template);
                }
                return;
              }
              if (isPolicyType(selectedValue)) {
                onChange(selectedValue);
                onTemplateSelect?.(null);
              }
            }}
          />
        ))
      )}
    </>
  );
}

/**
 * Compact description for cards within policy type rows
 * Simpler than OptionDescription - no environment badges since context is clear
 */
function CompactOptionDescription({
  description,
  compatibleGroupsCount,
  showCompatibleGroupsCount = true,
  isTemplate = false,
}: {
  description: string;
  compatibleGroupsCount: number;
  showCompatibleGroupsCount?: boolean;
  isTemplate?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      {/* Template badge */}
      {isTemplate && (
        <Badge type="info" className="w-fit">
          <FileText className="lucide-icon mr-1 !h-3 !w-3" />
          Template
        </Badge>
      )}

      {/* Description */}
      <div className="text-xs text-muted">{description}</div>

      {/* Groups count - hidden when total environment count > 100 (count is only based on first 100 envs) */}
      {showCompatibleGroupsCount && compatibleGroupsCount > 0 && (
        <div className="text-xs text-muted">
          {compatibleGroupsCount} environment{' '}
          {pluralize(compatibleGroupsCount, 'group')}
        </div>
      )}
    </div>
  );
}

/**
 * Represents a row of options for a single policy type
 */
interface PolicyTypeRowData {
  policyType: PolicyType;
  displayName: string;
  category: PolicyCategory;
  options: Array<BoxSelectorOption<string>>;
}

/**
 * Represents a section grouped by environment type (Kubernetes/Docker)
 */
interface EnvironmentSectionData {
  environmentType: 'kubernetes' | 'docker';
  displayName: string;
  policyTypeRows: Array<PolicyTypeRowData>;
}

/**
 * Group by environment type (Kubernetes/Docker), then by policy type within each
 */
function groupByEnvironmentType(
  policyTypes: Array<PolicyTypeMetadata>,
  templates: Array<PolicyTemplate>,
  environments: Environment[],
  groups: EnvironmentGroup[],
  showCompatibleGroupsCount: boolean
): Array<EnvironmentSectionData> {
  // Separate policy types by environment
  const k8sPolicyTypes = policyTypes.filter((p) => p.id.endsWith('-k8s'));
  const dockerPolicyTypes = policyTypes.filter((p) => p.id.endsWith('-docker'));

  const sections: Array<EnvironmentSectionData> = [];

  // Build Kubernetes section
  if (k8sPolicyTypes.length > 0) {
    const k8sRows = buildPolicyTypeRows(
      k8sPolicyTypes,
      templates,
      environments,
      groups,
      showCompatibleGroupsCount
    );
    sections.push({
      environmentType: 'kubernetes',
      displayName: 'Kubernetes',
      policyTypeRows: k8sRows,
    });
  }

  // Build Docker section
  if (dockerPolicyTypes.length > 0) {
    const dockerRows = buildPolicyTypeRows(
      dockerPolicyTypes,
      templates,
      environments,
      groups,
      showCompatibleGroupsCount
    );
    sections.push({
      environmentType: 'docker',
      displayName: 'Docker',
      policyTypeRows: dockerRows,
    });
  }

  return sections;
}

/**
 * Build policy type rows for a given set of policy types
 */
function buildPolicyTypeRows(
  policyTypes: Array<PolicyTypeMetadata>,
  templates: Array<PolicyTemplate>,
  environments: Environment[],
  groups: EnvironmentGroup[],
  showCompatibleGroupsCount: boolean
): Array<PolicyTypeRowData> {
  return policyTypes.map((metadata) => {
    const templatesForType = templates.filter((t) => t.type === metadata.id);

    const templateOptions = templatesForType.map((template) =>
      buildTemplateOption(
        template,
        environments,
        groups,
        showCompatibleGroupsCount
      )
    );
    const customOption = buildPolicyTypeOption(
      metadata,
      environments,
      groups,
      showCompatibleGroupsCount
    );
    const options: Array<BoxSelectorOption<string>> = [
      ...templateOptions,
      customOption,
    ];

    return {
      policyType: metadata.id,
      displayName: metadata.displayName,
      category: metadata.category,
      options,
    };
  });
}

/**
 * Renders a section for an environment type (Kubernetes/Docker)
 */
function EnvironmentSection({
  section,
  selectedValue,
  onSelect,
}: {
  section: EnvironmentSectionData;
  selectedValue: string;
  onSelect: (value: string) => void;
}) {
  const Icon = section.environmentType === 'kubernetes' ? Kubernetes : Docker;

  return (
    <div className="mb-6">
      <h3 className="flex items-center gap-2 text-base font-semibold mb-2 pb-2 border-b border-gray-5">
        <Icon className="w-7 h-7" />
        {section.displayName}
      </h3>
      <div className="space-y-3">
        {section.policyTypeRows.map((row) => (
          <PolicyTypeRow
            key={row.policyType}
            row={row}
            selectedValue={selectedValue}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Renders a row for a single policy type with visual distinction
 */
function PolicyTypeRow({
  row,
  selectedValue,
  onSelect,
}: {
  row: PolicyTypeRowData;
  selectedValue: string;
  onSelect: (value: string) => void;
}) {
  const Icon = getPolicyIcon(row.category);
  // Remove environment suffix from display name (e.g., "Kubernetes Security" → "Security")
  const shortName = getCategoryDisplayName(row.category);

  return (
    <div className="rounded-lg border border-solid border-gray-5 bg-gray-2 th-dark:bg-gray-iron-10 th-dark:border-gray-warm-9 p-3">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="lucide-icon !h-5 !w-5 text-gray-8 th-dark:text-gray-warm-4" />
        <h4 className="text-base font-semibold text-gray-8 th-dark:text-gray-warm-4 m-0">
          {shortName}
        </h4>
      </div>
      {/* Wrapper to make card headers smaller */}
      <div className="[&_.header]:!text-sm [&_.header]:!font-medium">
        <BoxSelector<string>
          radioName="policy-type"
          value={selectedValue}
          onChange={onSelect}
          options={row.options}
          useGridLayout
          className="-mb-3"
        />
      </div>
    </div>
  );
}

/**
 * Get short display name for a category (without environment prefix)
 */
function getCategoryDisplayName(category: PolicyCategory): string {
  switch (category) {
    case 'rbac':
      return 'RBAC';
    case 'security':
      return 'Security';
    case 'setup':
      return 'Setup';
    case 'registry':
      return 'Registry';
    default:
      return category;
  }
}

/**
 * Build a BoxSelectorOption for a policy type (custom from scratch)
 */
function buildPolicyTypeOption(
  metadata: PolicyTypeMetadata,
  environments: Environment[],
  groups: EnvironmentGroup[],
  showCompatibleGroupsCount: boolean
): BoxSelectorOption<string> {
  const compatibleGroupsCount = getCompatibleGroupsCount(
    metadata.id,
    environments,
    groups
  );

  return {
    id: metadata.id,
    value: metadata.id,
    label: 'Custom',
    icon: undefined, // Icon is shown in row header instead
    description: (
      <CompactOptionDescription
        description={metadata.description}
        compatibleGroupsCount={compatibleGroupsCount}
        showCompatibleGroupsCount={showCompatibleGroupsCount}
      />
    ),
  };
}

/**
 * Get icon for policy category
 */
function getPolicyIcon(category: PolicyCategory) {
  switch (category) {
    case 'rbac':
      return Lock;
    case 'security':
      return Shield;
    case 'setup':
      return Settings;
    case 'registry':
      return Database;
    default:
      return Shield;
  }
}

/**
 * Build a BoxSelectorOption for a policy template
 */
function buildTemplateOption(
  template: PolicyTemplate,
  environments: Environment[],
  groups: EnvironmentGroup[],
  showCompatibleGroupsCount: boolean
): BoxSelectorOption<string> {
  const compatibleGroupsCount = getCompatibleGroupsCount(
    template.type,
    environments,
    groups
  );

  return {
    id: `template:${template.id}`,
    value: `template:${template.id}`,
    label: template.name,
    icon: undefined, // Icon is shown in row header instead
    description: (
      <CompactOptionDescription
        description={template.description}
        compatibleGroupsCount={compatibleGroupsCount}
        showCompatibleGroupsCount={showCompatibleGroupsCount}
        isTemplate
      />
    ),
  };
}
